// src/pages/Campaigns/Campaigns.js
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Typography, Grid, Card, CardContent, CardActions,
  Button, Avatar
} from "@mui/material";
import {
  Google,
  MailOutline,
  Sms,
  Group,
  Event,
  People,
  Poll as SurveyIcon,
  ArrowForward as ArrowForwardIcon
} from "@mui/icons-material";

export default function Campaigns() {
  const navigate = useNavigate();
  const { chatbotId: paramsChatbotId } = useParams();

  useEffect(() => {
    document.title = "Campaigns | KlonIT Web Application";
  }, []);

  const cards = [
    {
      title: "Gmail Auto-Responder",
      description:
        "Connect your Gmail to automatically read and process incoming emails. Leverage AI to generate contextual replies.",
      icon: <Google sx={{ fontSize: 60, color: "#DB4437" }} />,
      link: `/chatbot/${paramsChatbotId}/campaigns/Gmail`,
      color: "#DB4437",
    },
    {
      title: "Outlook Auto-Responder",
      description:
        "Connect your Outlook to automatically read and process incoming emails. Leverage AI to generate contextual replies.",
      icon: <MailOutline sx={{ fontSize: 60, color: "#0078D4" }} />,
      link: `/chatbot/${paramsChatbotId}/campaigns/Outlook`,
      color: "#0078D4",
    },
    {
      title: "SMS Campaign",
      description:
        "Send automated SMS campaigns with AI-driven message generation and response handling.",
      icon: <Sms sx={{ fontSize: 60, color: "#1976d2" }} />,
      link: `/chatbot/${paramsChatbotId}/campaigns/Sms`,
      color: "#1976d2",
    },
    {
      title: "Lead Management",
      description:
        "Connect your Salesforce or other CRM to manage and track customer leads efficiently.",
      icon: <Group sx={{ fontSize: 60, color: "#2e7d32" }} />,
      link: `/chatbot/${paramsChatbotId}/campaigns/Lead`,
      color: "#2e7d32",
    },
    {
      title: "Volunteer Management",
      description:
        "Manage volunteers, track their availability, skills, and coordinate volunteer assignments.",
      icon: <People sx={{ fontSize: 60, color: "#4ECDC4" }} />,
      link: `/chatbot/${paramsChatbotId}/campaigns/volunteers`,
      color: "#4ECDC4",
    },
    {
      title: "Event Management",
      description:
        "Create and manage events, track attendance, send invitations, and handle event logistics.",
      icon: <Event sx={{ fontSize: 60, color: "#FF6B6B" }} />,
      link: `/chatbot/${paramsChatbotId}/campaigns/events`,
      color: "#FF6B6B",
    },
    {
      title: "SurveyGenie",
      description:
        "Create and manage surveys with advanced question types, distribution options, and comprehensive analytics.",
      icon: <SurveyIcon sx={{ fontSize: 60, color: "#9C27B0" }} />,
      link: `/chatbot/${paramsChatbotId}/surveys`,
      color: "#9C27B0",
    },
  ];

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700}>
          Campaigns
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Marketing campaigns, outreach, and automation tools
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "0.3s",
                cursor: "pointer",
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-4px)",
                },
              }}
              onClick={() => navigate(card.link)}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: "center", pt: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: `${card.color}15`,
                    margin: "0 auto 16px",
                  }}
                >
                  {card.icon}
                </Avatar>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  {card.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: "center", pb: 3 }}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ bgcolor: card.color }}
                >
                  Open
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
